// Building comment bodies (SPEC §6): suggestions on line anchors, the Hashquill marker on
// file-level comments, and neutralizing suggestion fences wherever a comment is file-level.
import { parseMarkdown, startOf, walk } from '../markdown/parse';

/** Exact head source lines of the anchor range (pre-fills the suggestion editor). */
export function linesOf(head: string, startLine: number, endLine: number): string {
  return head.split('\n').slice(startLine - 1, endLine).join('\n');
}

/** A fence that can't be closed by the suggested text itself. */
function fenceFor(text: string): string {
  const longest = Math.max(2, ...[...text.matchAll(/^\s*(`{3,})/gm)].map((m) => m[1]!.length));
  return '`'.repeat(longest + 1);
}

export function lineCommentBody(comment: string, suggestion?: string): string {
  const c = comment.trim();
  if (suggestion === undefined) return c;
  const f = fenceFor(suggestion);
  return `${c ? `${c}\n\n` : ''}${f}suggestion\n${suggestion}\n${f}`;
}

/**
 * File-level body with the hidden marker so Hashquill can re-anchor it:
 *   <!-- hashquill: file=… lines=a-b sha=… -->
 *   > first ~200 chars of the block…
 *
 *   Comment text
 */
export function fileCommentBody(opts: { path: string; startLine: number; endLine: number; headSha: string; quote: string; comment: string }): string {
  const q = opts.quote.replace(/\s+/g, ' ').trim();
  const quote = q.length > 200 ? `${q.slice(0, 200)}…` : q;
  const marker = `<!-- hashquill: file=${opts.path} lines=${opts.startLine}-${opts.endLine} sha=${opts.headSha} -->`;
  return `${marker}\n> ${quote}\n\n${neutralizeSuggestions(opts.comment.trim()).text}`;
}

/**
 * Suggestions only work on comments attached to changed lines. On a FILE comment GitHub
 * renders them against line 1 (spike test 4), so every `suggestion` fence is rewritten to
 * `text`, found with the Markdown parser so every fence variant is caught.
 */
export function neutralizeSuggestions(body: string): { text: string; changed: boolean } {
  const tree = parseMarkdown(body);
  const edits: number[] = []; // offsets of the info-string start
  for (const { node } of walk(tree)) {
    if (node.type !== 'code' || !node.lang || node.lang.toLowerCase() !== 'suggestion') continue;
    const at = startOf(node);
    const m = /^(\s*)(`{3,}|~{3,})(\s*)/.exec(body.slice(at));
    if (m) edits.push(at + m[0].length);
  }
  let text = body;
  for (const at of edits.sort((a, b) => b - a)) text = `${text.slice(0, at)}text${text.slice(at + 'suggestion'.length)}`;
  return { text, changed: edits.length > 0 };
}

export const SUGGESTION_WARNING =
  'Suggestions only work on comments attached to changed lines. This comment is on the whole file, so your suggestion will be posted as a plain code block.';
